import React, { useState, useRef, useCallback } from "react";
import { DownArrow, Menu } from "../../assets/icons/Icons";
import { IoWarningOutline } from "react-icons/io5";
import ProfilePicture from "../atoms/ProfilePicture";
import SwitchBranch from "./modals/SwitchBranch";
import { modalStyles, switchBranch } from "../../assets/styles/modalStyles";
import MobileSidebar from "./MobileSidebar";
import { useHospDocData } from "../../lib/contexts/HospitalDoctorContext";
import ClinicModal from "./modals/HospModal";
import { useUserData } from "../../lib/contexts/UserContext";
import NotificationBell from "./notifications/NotificationBell";
import Notifications from "./notifications/Notifications";
import moment from "moment";
import {
  getNotificationsCount,
  getUnreadNotificationsCount,
} from "../../lib/apis/notifications";
import { useFeatureFlags } from "../../lib/contexts/FeatureFlagContext";
import { Navigate, useNavigate } from "react-router-dom";
import { MdKeyboardArrowRight } from "react-icons/md";
import BrandLogo from "../atoms/BrandLogo";
import { useSidebar } from "../../lib/contexts/SidebarContext";

const MainHeader = ({ 
  value,
  breadcrumb,
  backLink
  }: { 
  value?: string;
  breadcrumb?:string;
  backLink?:string;
  }) => {
  const navigate = useNavigate();
  const { allHospData, hospData, hospitalID } = useHospDocData();
  const { userData } = useUserData();
  const { isCollapsed } = useSidebar();
  const [switchVisible, setSwitchVisible] = useState(false);
  const [clinicVisible, setClinicVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const prevNotificationCountRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { notification } = useFeatureFlags();

  // Track user interaction to enable audio playback
  React.useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      // Remove listeners after first interaction
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);

    return () => {
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  // Refactor notification count fetching into a function
  const fetchUnreadCount = useCallback(async () => {
    if (userData?.role !== "Doctor" && userData?.user_id) {
      try {
          const count = await getUnreadNotificationsCount(userData?.user_id, false);
          const storedCount = localStorage.getItem(`notification_count_${userData?.user_id}`) || "0";
          const prevCount = parseInt(storedCount, 10);

          // Audio Logic
          const isMuted = localStorage.getItem("notifications_muted") === "true";
          
          if (count > prevCount && hasUserInteracted && !isMuted && notification) {
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().catch(e => console.log("Audio error", e));
            }
          }

          localStorage.setItem(`notification_count_${userData?.user_id}`, count.toString());
          setNotificationCount(count);
      } catch (error) {
          console.error("Notification fetch error", error);
      }
    }
  }, [userData?.role, userData?.user_id, hasUserInteracted, notification]);

  React.useEffect(() => {
    if (hospData?.trial_end_date && userData?.role === "Trial") {
      const msInDay = 1000 * 60 * 60 * 24;
      const trialEndDateIST = moment
        .utc(hospData?.trial_end_date)
        .utcOffset("+05:30");
      setDaysRemaining(
        Math.max(0, Math.ceil(trialEndDateIST.diff(moment()) / msInDay))
      );
      if (
        Math.ceil(trialEndDateIST.diff(moment()) / msInDay) <= 0 &&
        window.location.pathname !== `/${hospitalID}/dashboard`
      ) {
        window.location.pathname = `/${hospitalID}/dashboard`;
      }
    }
    // Fetch notification count
    fetchUnreadCount();
    const intervalId = setInterval(() => {
        fetchUnreadCount();
    }, 120000); // 2 minutes (was 30s — the badge doesn't need 30s freshness)

    return () => clearInterval(intervalId);
  }, [
    hospData,
    hospitalID,
    userData?.role,
    fetchUnreadCount,
    hasUserInteracted,
  ]);

  return (
    <>
      <audio
        ref={audioRef}
        src="https://ik.imagekit.io/gn9by35ed/new-notification-09-352705.mp3?updatedAt=1753245591802"
        preload="auto"
        style={{ display: "none" }}
      />
      <div className="bg-white h-[64px] px-5 lg:pl-7 lg:pr-11 py-5 flex justify-between items-center border-solid border-b-[1px] border-sbBorder">
        <div className="flex flex-row items-center">
          <button
            className="flex xl:hidden mr-3"
            onClick={() => setMenuVisible(true)}
          >
            <Menu />
          </button>
          {/* Logo here only when the sidebar is collapsed, or on tab/mobile
              where the sidebar is hidden (xl:hidden). */}
          <div
            className={`items-center ${isCollapsed ? "flex" : "flex xl:hidden"}`}
          >
            <BrandLogo className="mr-3" />
            <span className="hidden md:block h-6 w-px bg-gray-200 mr-3" />
          </div>
          <div className="flex items-center">
            <p 
              onClick={() => backLink && navigate(backLink)}
              className={`text-xl ${backLink ? "cursor-pointer text-gray-500 hover:text-gray-700 font-normal" : "font-semibold text-gray-900"}`}
            >
              {value}
            </p>

            {breadcrumb && (
              <>
                <MdKeyboardArrowRight className="mx-2 text-gray-400 text-2xl" />
                <p className="font-semibold text-xl text-gray-900">{breadcrumb}</p>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-row items-center">
          {notification && userData?.role !== "Doctor" && (
            <div className="mr-4">
              <NotificationBell
                newNotifications={notificationCount}
                onClick={() => setNotificationsOpen(true)}
              />
            </div>
          )}

          {userData?.role === "Trial" && (
            <div className="p-4 py-2 border-orange-300 border-2 rounded-lg bg-yellow-100 flex gap-1 items-center">
              <IoWarningOutline className="w-5 h-5 text-red-600" />{" "}
              <p className="text-sm">
                Free trials end in{" "}
                <span className="text-red-600">{daysRemaining} days</span>
              </p>
            </div>
          )}
          <button
            onClick={() => {
              if (allHospData) {
                if (allHospData.length > 1) {
                  setSwitchVisible(true);
                  setClinicVisible(false);
                } else if (hospData !== undefined) {
                  setSwitchVisible(false);
                  setClinicVisible(true);
                } else {
                  setClinicVisible(false);
                  setSwitchVisible(false);
                }
              }
            }}
            className="flex flex-row items-center ml-2 md:ml-4"
          >
            {hospData?.logo === "test" || hospData?.logo === "" ? (
              <ProfilePicture
                username={hospData.hospital_name}
                is_active={null}
              />
            ) : (
              <img
                className={`w-10 md:w-12 h-10 md:h-12 rounded-full`}
                src={hospData?.logo}
                alt="hospital-logo"
              ></img>
            )}
            <p className="hidden lg:block font-medium text-sm ml-2 lg:ml-2 mr-3">
              {hospData ? hospData.hospital_name : ""}
            </p>
            {allHospData && allHospData.length > 1 && <DownArrow />}
          </button>
        </div>
        <SwitchBranch
          modalIsOpen={switchVisible}
          closeModal={() => setSwitchVisible(false)}
          customStyles={switchBranch}
        />
        <ClinicModal
          modalIsOpen={clinicVisible}
          closeModal={() => setClinicVisible(false)}
          customStyles={modalStyles}
        />
        {menuVisible && (
          <MobileSidebar visible={menuVisible} setVisible={setMenuVisible} />
        )}
      </div>
      {notification && (
        <Notifications
          isOpen={isNotificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          userId={userData?.user_id}
          onStateChange={fetchUnreadCount}
        />
      )}
    </>
  );
};

export default MainHeader;
